import React, { useEffect, useMemo, useRef, useState } from "react";
import GenericDialog from "@components/GenericDialog/GenericDialog";
import LoadingOverlay from "@components/LoadingOverlay/LoadingOverlay";
import StepperTabPanel from "@components/StepperTabs/StepperTabPanel";
import StepperTabs from "@components/StepperTabs/StepperTabs";
import { STEPPER_TYPE } from "@components/StepperTabs/StepperTabs.types";
import type { StepperTabsHandle } from "@components/StepperTabs/StepperTabs.types";
import UnauthorizedRedirection from "@components/UnauthorizedRedirection/UnauthorizedRedirection.tsx";
import { useNotification } from "@context/NotificationContext";
import { useCheckOwnersMatter } from "@features/vessel-call/vessel-call-status/hooks/useCheckOwnersMatter.ts";
import { useCheckProposedAgency } from "@features/vessel-call/vessel-call-status/hooks/useCheckProposedAgency.ts";
import {
  buildVesselHeaderDetails,
  TO_BERTH_TYPES,
} from "@features/vessel-call/vessel-call-status/utils.tsx";
import VesselCallHeader from "@features/vessel-call/vessel-call-status/VesselCallHeader.tsx";
import type {
  VesselCallError,
  VesselHeaderDTO,
} from "@features/vessel-call/vessel-call-status/VesselCallStatus.types.tsx";
import { useFormattedReference } from "@hooks/useFormattedReference";
import PageLayout from "@layouts/PageLayout";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { uploadFileToAzureBlob } from "@services/azureDataApi";
import { getDepartureCallById } from "@services/departureServiceApi.ts";
import { getDisputes } from "@services/disputesApi";
import { getPortServices } from "@services/portServicesApi.ts";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import { SasTokenPrefix, SasTokenPrivilege } from "@services/user/types";
import { getSasToken } from "@services/userApi";
import type { Company, PortCode, VesselTypes } from "@services/vessel-call/types";
import type { Vessel } from "@services/vessel/types";
import type {
  CloseVesselCallByIdArgs,
  VesselCallResponse,
  VesselRegisterDetailsFixed,
} from "@services/vesselCall/types.tsx";
import { getVesselCallById, getVesselCallDetails } from "@services/vesselCallApi";
import { getVesselByIdNoCompanyIdValidation } from "@services/vesselsApi";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { addDays } from "@utils/date-time.tsx";
import { paths } from "@utils/navigation";
import { checkPermission } from "@utils/user-permissions";
import { useTranslation } from "react-i18next";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import {
  unavailableDepartureAvailability,
  type DepartureAvailability,
} from "./Departure/availability/departureAvailability";
import { useDepartureAvailability } from "./Departure/hooks/useDepartureAvailability";
import ApproveVesselCallDrawer from "./flow/approve/ApproveVesselCallDrawer.tsx";
import CloseVesselCallDrawer from "./flow/CloseVesselCallDrawer";
import InterruptVesselCallDrawer from "./flow/interrupt/InterruptVesselCallDrawer.tsx";
import PreApproveVesselCallDrawer from "./flow/pre-approve/PreApproveVesselCallDrawer";
import PreRejectVesselCallDrawer from "./flow/pre-reject/PreRejectVesselCallDrawer.tsx";
import RejectVesselCallDrawer from "./flow/reject/RejectVesselCallDrawer.tsx";
import ResumeVesselCallDrawer from "./flow/resume/ResumeVesselCallDrawer.tsx";
import { canShowCloseVesselCallActions, VESSEL_CALL_STATUSES } from "./flow/utils.tsx";
import { useCloseVesselCall } from "./hooks/useCloseVesselCall.tsx";
import { useCompleteVesselCall } from "./hooks/useCompleteVesselCall.tsx";
import { useCurrentMovements } from "./hooks/useCurrentMovements";
import { useInterruptVesselCall } from "./hooks/useInterruptVesselCall.tsx";
import { usePastMovements } from "./hooks/usePastMovements";
import { useResumeVesselCallById } from "./hooks/useResumeInterruptionVesselCall.tsx";
import { MOVEMENT_SERVICE_STATUSES } from "./MovementServices/flow/utils.tsx";
import type { BadgeType } from "./MovementServices/preview/components/MovementServiceSummary.tsx";
import type { PortServicesData } from "./PortServices/grid/PortServices.tsx";
import StatusSideDrawer from "./StatusSideDrawer.tsx";
import { DEPARTURE_STATUSES, shouldUseVesselLocationForArrivalStatus } from "./utils.tsx";
import UpdateVesselCallSection from "./VesselCallDetails/UpdateVesselCallSection.tsx";

const TEST_ID_BASE = "vessel-status-page";
const LANG_BASE = "vessel-call.vessel-status";

const serviceVessel = "bff-vessel";
const serviceVesselCall = "bff-vessel-call";
const service = "bff-vessel-call";
export interface VesselCallStatusContextType {
  vesselRegisterDetails: Vessel;
  vesselCallDetails: VesselCallResponse;
  vesselRegisterDetailsFixed?: VesselRegisterDetailsFixed | null;
  isThirdShippingAgent: boolean;
  isOwnersMatter: boolean;
  canAccessOperations: boolean;
  isOperationsAccessLoading: boolean;
  departureAvailability?: DepartureAvailability;
  refetchDepartureAvailability: () => Promise<DepartureAvailability | undefined>;
}

export type VesselCallDrawerType =
  | "preApproveDrawer"
  | "preRejectDrawer"
  | "approveDrawer"
  | "rejectDrawer"
  | "statusDrawer"
  | "interruptDrawer"
  | "resumeVesselCallDrawer";

const VesselCallStatus: React.FC = () => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [openDrawers, setOpenDrawers] = useState<{ [key: string]: boolean }>({
    preApproveDrawer: false,
    preRejectDrawer: false,
    approveDrawer: false,
    rejectDrawer: false,
    statusDrawer: false,
    interruptDrawer: false,
    resumeVesselCallDrawer: false,
    closeVesselCallDrawer: false,
  });
  const { notify } = useNotification();
  const { userActionPermissions, user, isShippingAgentUser, isMarineDepartmentUser } =
    useAuthStore();

  const handleOpenDrawer = (drawer: string) =>
    setOpenDrawers((prev) => ({ ...prev, [drawer]: true }));

  const handleCloseDrawer = (drawer: string) =>
    setOpenDrawers((prev) => ({ ...prev, [drawer]: false }));

  const handleCloseAllDrawers = () =>
    setOpenDrawers({
      preApproveDrawer: false,
      preRejectDrawer: false,
      approveDrawer: false,
      rejectDrawer: false,
      statusDrawer: false,
      interruptDrawer: false,
      resumeVesselCallDrawer: false,
      closeVesselCallDrawer: false,
    });

  const {
    data: vesselCallDetails,
    isLoading: isVesselDataLoading,
    refetch: refetchVesselCall,
    error: vesselCallError,
  } = useQuery({
    queryKey: ["vesselCall", serviceVesselCall, id],
    queryFn: () => getVesselCallById(serviceVesselCall, id as string),
    enabled: !!id,
    refetchInterval: 60_000,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
  const { data: activeDisputesData } = useQuery({
    queryKey: ["disputes", serviceVesselCall, id, "active"],
    queryFn: () =>
      getDisputes({
        service: serviceVesselCall,
        payload: {
          vesselCallId: id ?? "",
          status: ["DISPUTE_RAISED", "RESOLUTION_PENDING"],
        },
        page: 0,
        pageSize: 1,
      }),
    enabled: Boolean(id),
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
  const { data: vesselRegisterDetails, isLoading: isVesselRegisterDataLoading } = useQuery<Vessel>({
    queryKey: ["vessel", serviceVessel, vesselCallDetails?.vesselId],
    queryFn: () =>
      getVesselByIdNoCompanyIdValidation(serviceVessel, vesselCallDetails?.vesselId as string),
    enabled: !!vesselCallDetails?.vesselId,
  });

  const { data: portServicesData } = useQuery<PortServicesData>({
    queryKey: ["getPortServices", 1, 25, vesselCallDetails?.vesselId],
    queryFn: () => {
      return getPortServices({
        service,
        page: 0,
        pageSize: 25,
        filters: {
          vesselCallId: id,
          vesselCallNo: vesselCallDetails?.vcn ? [vesselCallDetails.vcn] : undefined,
        },
      });
    },
    staleTime: 0,
  });

  const isThirdShippingAgent = isShippingAgentUser
    ? vesselCallDetails?.companyId !== user?.companyId && (portServicesData?.totalElements ?? 0) > 0
    : false;
  const isOwnersMatter = useCheckOwnersMatter(id as string);
  const isProposedAgency = useCheckProposedAgency(id as string);

  const isTabHiddenAfterTransfer = useMemo(() => {
    return isProposedAgency && !isOwnersMatter;
  }, [isProposedAgency, isOwnersMatter]);

  const navigate = useNavigate();
  // Reroute from vessel-details to port-services if isOwnersMatter is true

  useEffect(() => {
    if (
      (isOwnersMatter || isThirdShippingAgent) &&
      location.pathname.includes(`/vessel-call/vessel-call-status/${id}/vessel-details`)
    ) {
      navigate(`/vessel-call/vessel-call-status/${id}/port-services`, { replace: true });
    } else if (
      isProposedAgency &&
      location.pathname.includes(`/vessel-call/vessel-call-status/${id}/vessel-details`)
    ) {
      navigate(`/vessel-call/vessel-call-status/${id}/shipping-agency-transfer`, { replace: true });
    }
  }, [isOwnersMatter, id, navigate, isThirdShippingAgent, isProposedAgency]);
  const [fakeOverrideStatus, setFakeOverrideStatus] = useState<string | undefined>("");
  const [fakeStatus, setFakeStatus] = useState<string | null | undefined>("");
  const isPortServicesResolved = !isShippingAgentUser || portServicesData !== undefined;
  const canEvaluateOperationsAccess =
    Boolean(vesselCallDetails?.vesselId) && isPortServicesResolved;

  const { data: currentMovementsData, isLoading: isCurrentMovementsLoading } = useCurrentMovements({
    service: serviceVesselCall,
    vesselCallId: id,
    vesselId: vesselCallDetails?.vesselId,
    enabled: isPortServicesResolved && !isThirdShippingAgent && !isOwnersMatter,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  const { data: pastMovementsData, isLoading: isPastMovementsLoading } = usePastMovements({
    service: serviceVesselCall,
    vesselCallId: id,
    vesselId: vesselCallDetails?.vesselId,
    enabled: isPortServicesResolved && !isThirdShippingAgent && !isOwnersMatter,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  const { data: departureDetails } = useQuery({
    queryKey: ["vessel-departure-details", id],
    queryFn: () => getDepartureCallById(id as string),
    enabled: isPortServicesResolved && !isThirdShippingAgent && !isOwnersMatter,
  });

  const { availability: departureAvailability, refetch: refetchDepartureAvailabilityQuery } =
    useDepartureAvailability(id, vesselCallDetails?.vesselId);

  const refetchDepartureAvailability = async () => {
    const result = await refetchDepartureAvailabilityQuery();
    return result.isError ? unavailableDepartureAvailability() : result.data;
  };

  const departureTabIsHidden = useMemo(() => {
    const hasClosedMovement =
      pastMovementsData?.items?.some(
        (movement) => movement.status === MOVEMENT_SERVICE_STATUSES.CLOSED,
      ) ?? false;
    const hasDepartureDetails = !!departureDetails;
    if (!hasClosedMovement) {
      return true;
    }
    if (hasDepartureDetails) {
      return false;
    }
    if (!checkPermission(userActionPermissions, "canRequestDeparture")) {
      return true;
    }
    return false;
  }, [departureDetails, pastMovementsData, userActionPermissions]);

  const [isUpdateSectionVisible, setIsUpdateSectionVisible] = useState(false);

  const handleToggleUpdateVesselCall = () => {
    setIsUpdateSectionVisible(true);
  };

  const handleUpdateSectionSubmitted = () => {
    setIsUpdateSectionVisible(false);
  };

  const isAtsSet = useMemo(() => {
    return currentMovementsData?.[0]?.ats !== null && currentMovementsData?.[0]?.ats !== undefined;
  }, [currentMovementsData]);

  const isAtcSet = useMemo(() => {
    return pastMovementsData?.items.some(
      (movement) => movement.atc !== null && movement.atc !== undefined,
    );
  }, [pastMovementsData]);

  const isOperationsAccessLoading = useMemo(() => {
    return !canEvaluateOperationsAccess || Boolean(isPastMovementsLoading);
  }, [canEvaluateOperationsAccess, isPastMovementsLoading]);

  const canAccessOperations = useMemo(() => {
    if (!canEvaluateOperationsAccess) {
      return false;
    }

    return (
      pastMovementsData?.items.some(
        (movement) =>
          movement.status === MOVEMENT_SERVICE_STATUSES.CLOSED &&
          TO_BERTH_TYPES.has(movement.movementType || ""),
      ) ?? false
    );
  }, [canEvaluateOperationsAccess, pastMovementsData]);

  const canUpdateVesselCall =
    vesselCallDetails?.status === "APPROVED" &&
    !isCurrentMovementsLoading &&
    !isPastMovementsLoading &&
    !isAtcSet &&
    !isAtsSet;

  const showUpdateButton = canUpdateVesselCall && !isUpdateSectionVisible;

  const isStatusBeforeApproved =
    vesselCallDetails?.status === "PENDING_PRE_APPROVAL" ||
    vesselCallDetails?.status === "PRE_APPROVED" ||
    vesselCallDetails?.status === "REJECTED_MARINE_SUPERVISOR" ||
    vesselCallDetails?.status === "PENDING_APPROVAL" ||
    vesselCallDetails?.status === "REJECTED_MARINE_OPERATOR";

  const isDraft =
    vesselCallDetails?.status === "ARRIVAL_DRAFT" ||
    vesselCallDetails?.status === "PRE_ARRIVAL_DRAFT";

  const stepperTabsRef = useRef<StepperTabsHandle>(null);
  const { i18n, t } = useTranslation();

  const canSeeVesselDetails = !isOwnersMatter && !isThirdShippingAgent && !isProposedAgency;
  const canSeeMovementServices =
    !isStatusBeforeApproved &&
    !isTabHiddenAfterTransfer &&
    !isOwnersMatter &&
    !isDraft &&
    !isThirdShippingAgent;
  const canSeePortServices =
    (vesselCallDetails?.status !== "PENDING_PRE_APPROVAL" &&
      vesselCallDetails?.status !== "REJECTED_MARINE_SUPERVISOR" &&
      !isTabHiddenAfterTransfer &&
      !isDraft &&
      !isProposedAgency) ||
    isThirdShippingAgent ||
    isOwnersMatter;
  const canSeeDeparture =
    !departureTabIsHidden &&
    !isStatusBeforeApproved &&
    !isOwnersMatter &&
    !isDraft &&
    !isThirdShippingAgent;
  const canSeeShippingAgencyTransfer =
    !isStatusBeforeApproved &&
    ((!isOwnersMatter && !isThirdShippingAgent) || isProposedAgency) &&
    !isDraft;
  const canSeeOperations =
    canAccessOperations &&
    !isStatusBeforeApproved &&
    !isOwnersMatter &&
    !isDraft &&
    !isThirdShippingAgent;

  const vesselCallStatusSteps = [
    ...(canSeeVesselDetails
      ? [{ label: t(`${LANG_BASE}.vessel-details`), path: paths.vesselCall.vesselDetails }]
      : []),
    ...(canSeeMovementServices
      ? [{ label: t(`${LANG_BASE}.movement-services`), path: paths.vesselCall.movementServices }]
      : []),
    ...(canSeePortServices
      ? [{ label: t(`${LANG_BASE}.port-services`), path: paths.vesselCall.portServices }]
      : []),
    ...(canSeeOperations
      ? [{ label: t(`${LANG_BASE}.operations`), path: paths.vesselCall.operations.index }]
      : []),
    ...(canSeeDeparture
      ? [
          {
            label: t(`${LANG_BASE}.departure`),
            path: paths.vesselCall.departure,
          },
        ]
      : []),
    ...(canSeeShippingAgencyTransfer
      ? [
          {
            label: t(`${LANG_BASE}.shipping-agency-transfer`),
            path: paths.vesselCall.shippingAgencyTransfer,
          },
        ]
      : []),
  ];

  const [externalAttachmentsUrl, setExternalAttachmentsUrl] = useState<string>();
  const [externalAttachmentsName, setExternalAttachmentsName] = useState<string>();
  const [internalAttachmentsUrl, setInternalAttachmentsUrl] = useState<string>();
  const [internalAttachmentsName, setInternalAttachmentsName] = useState<string>();
  const [rejectionAttachmentsUrl, setRejectionAttachmentsUrl] = useState<string>();
  const [rejectionAttachmentsName, setRejectionAttachmentsName] = useState<string>();

  const handleSuccess = async (status?: string) => {
    await refetchVesselCall();
    if (status === "APPROVED") {
      if (vesselCallDetails?.status === "PENDING_PRE_APPROVAL") {
        setFakeStatus("PRE_APPROVED");
        notify({
          type: "success",
          title: t("vessel-call.pre-approve.success-toast", {
            vesselName: vesselRegisterDetailsFixed?.vesselName,
          }),
        });
      } else {
        setFakeStatus("APPROVED");
      }
    }
    if (status === "REJECTED") {
      if (vesselCallDetails?.status === "PENDING_PRE_APPROVAL") {
        setFakeStatus("REJECTED_MARINE_SUPERVISOR");
      } else {
        setFakeStatus("REJECTED_MARINE_OPERATOR");
      }
    }
    if (status === "INTERRUPTED_LEAVE_PORT") {
      setFakeStatus("INTERRUPTED_LEAVE_PORT");
    }
    if (status === "INTERRUPTED_IN_PORT") {
      setFakeStatus("INTERRUPTED_IN_PORT");
    }
    if (status === "INTERRUPTED") {
      setFakeStatus("INTERRUPTED");
    }
    if (status === "RESUME_PENDING") {
      setFakeStatus("RESUME_PENDING");
    }
    if (status === "CLOSED") {
      setFakeStatus("CLOSED");
    }
    // Invalidate the specific vessel-call query and related cached data
    queryClient.invalidateQueries({ queryKey: ["vessel"] });
    queryClient.invalidateQueries({ queryKey: ["getCurrentMovementService", id] });
    queryClient.invalidateQueries({ queryKey: ["task"] });
    queryClient.invalidateQueries({ queryKey: ["vessels"] });
    queryClient.invalidateQueries({ queryKey: ["registeredVessels"] });
    queryClient.invalidateQueries({ queryKey: ["vesselCall"] });
  };

  const handleError = async (error: VesselCallError) => {
    await refetchVesselCall();
    notify({
      title: t("common.error"),
      description:
        error?.response?.data?.detail ?? error?.message ?? t("common.something-went-wrong"),
      type: "error",
    });
  };

  const { completeCall } = useCompleteVesselCall({ handleSuccess, handleError });
  const { closeCall } = useCloseVesselCall({ handleSuccess, handleError });
  const { interruptCall } = useInterruptVesselCall({ handleSuccess });

  const documentUploadMutation = useMutation({
    mutationFn: ({ file, documentType }: { file: File; documentType: string }) =>
      runTransaction(
        {
          module: "vessel_call",
          screen: "VesselCallStatus",
          action: "upload_vessel_call_document",
          entityType: "vessel_call_document",
        },
        async (transactionId) => {
          const payloadForSasToken = {
            prefix: SasTokenPrefix.VESSEL_REGISTRATION,
            contentType: file.type || null,
            privilege: SasTokenPrivilege.WRITE,
            blobUrl: null,
          };

          const { blobUrl, sasToken } = await getSasToken(
            payloadForSasToken,
            ...(transactionId ? [transactionId] : []),
          );
          const [uploadResult] = await uploadFileToAzureBlob({
            file,
            blobUrl,
            sasToken,
            certificateType: documentType,
          });

          const documentUrl = uploadResult?.documentUrl ?? blobUrl;
          return { documentUrl, documentType, documentName: file.name };
        },
      ),
  });

  const handleDocumentUpload = (file: File, documentType: string) => {
    documentUploadMutation.mutate(
      { file, documentType },
      {
        onSuccess: ({ documentUrl, documentType: currentType, documentName }) => {
          if (currentType === "INTERNAL_NOTES") {
            setInternalAttachmentsUrl(documentUrl);
            setInternalAttachmentsName(documentName);
          } else if (currentType === "REJECTION_NOTES") {
            setRejectionAttachmentsUrl(documentUrl);
            setRejectionAttachmentsName(documentName);
          } else if (currentType === "EXTERNAL_NOTES") {
            setExternalAttachmentsUrl(documentUrl);
            setExternalAttachmentsName(documentName);
          }
        },
      },
    );
  };

  type InterruptPayload = {
    forceLeave: boolean;
    externalNotes: string;
    internalNotes: string;
    internalAttachmentUrl: string | undefined;
    internalAttachmentName?: string;
    externalAttachmentUrl: string | undefined;
    externalAttachmentName?: string;
  };
  const [isInterruptConfirmOpen, setIsInterruptConfirmOpen] = useState(false);
  const interruptPendingPayloadRef = useRef<InterruptPayload>(null);

  const handleInterruptClick = (payload?: InterruptPayload) => {
    interruptPendingPayloadRef.current = payload ?? null;
    handleCloseDrawer("interruptDrawer");
    setIsInterruptConfirmOpen(true);
  };

  const handleInterruptionCall = async ({
    internalNotes,
    externalNotes,
    internalAttachmentsUrl,
    internalAttachmentsName,
    externalAttachmentsUrl,
    externalAttachmentsName,
    isVesselForcedToLeavePort,
  }: {
    internalNotes: string;
    externalNotes: string;
    internalAttachmentsUrl: string | undefined;
    internalAttachmentsName?: string;
    externalAttachmentsUrl: string | undefined;
    externalAttachmentsName?: string;
    isVesselForcedToLeavePort: boolean;
  }) => {
    await interruptCall({
      service: serviceVesselCall,
      vesselCallId: id || "",
      params: {
        internalNotes,
        externalNotes,
        internalAttachmentsUrl,
        internalAttachmentsName,
        externalAttachmentsUrl,
        externalAttachmentsName,
        isVesselForcedToLeavePort,
      },
    });
  };

  const invalidateInterruption = async () => {
    await queryClient.invalidateQueries({
      queryKey: ["vesselInterruptions", serviceVesselCall],
      refetchType: "all",
    });
  };

  const handleConfirmInterrupt = async () => {
    if (!interruptPendingPayloadRef.current) {
      return;
    }

    await handleInterruptionCall({
      internalNotes: interruptPendingPayloadRef.current.internalNotes,
      externalNotes: interruptPendingPayloadRef.current.externalNotes,
      internalAttachmentsUrl: interruptPendingPayloadRef.current.internalAttachmentUrl,
      internalAttachmentsName:
        interruptPendingPayloadRef.current.internalAttachmentName ?? internalAttachmentsName,
      externalAttachmentsUrl: interruptPendingPayloadRef.current.externalAttachmentUrl,
      externalAttachmentsName:
        interruptPendingPayloadRef.current.externalAttachmentName ?? externalAttachmentsName,
      isVesselForcedToLeavePort: interruptPendingPayloadRef.current.forceLeave,
    });
    notify({
      title: t("vessel-call.interruption.sticky-notification-title"),
      type: "success",
    });
    setIsInterruptConfirmOpen(false);
    setFakeOverrideStatus(
      interruptPendingPayloadRef.current.forceLeave
        ? "INTERRUPTED_LEAVE_PORT"
        : "INTERRUPTED_IN_PORT",
    );
    await invalidateInterruption();
    interruptPendingPayloadRef.current = null;
  };

  type VesselCallDecisionPayload = {
    rejectionReason?: string;
    externalNotes?: string;
    internalNotes?: string;
    hasRejectionAttachment?: boolean;
    hasInternalAttachment?: boolean;
    hasExternalAttachment?: boolean;
    rejectionAttachmentName?: string;
    internalAttachmentName?: string;
    externalAttachmentName?: string;
    status: string;
    reEntryVessel?: boolean;
    overstayChargesApplicable?: boolean;
    assignedAnchorageArea?: string;
  };

  const handleCloseVesselCall = async ({
    externalNotes = "",
    internalNotes = "",
    hasInternalAttachment = false,
    hasExternalAttachment = false,
    internalAttachmentName,
    externalAttachmentName,
  }: VesselCallDecisionPayload) => {
    const payload: CloseVesselCallByIdArgs = {
      submittedBy: user?.preferredUsername ?? "",
      submittedByRole: user?.roles?.[0] ?? "",
      externalNotes,
      externalAttachments:
        hasExternalAttachment && externalAttachmentsUrl
          ? [
              {
                url: externalAttachmentsUrl,
                type: "EXTERNAL_NOTES",
                originalFileName: externalAttachmentName ?? externalAttachmentsName ?? "",
              },
            ]
          : [],
      internalNotes,
      internalAttachments:
        hasInternalAttachment && internalAttachmentsUrl
          ? [
              {
                url: internalAttachmentsUrl,
                type: "INTERNAL_NOTES",
                originalFileName: internalAttachmentName ?? internalAttachmentsName ?? "",
              },
            ]
          : [],
    };

    await closeCall({
      service: serviceVesselCall,
      vesselCallId: id || "",
      payload,
    });
    handleCloseAllDrawers();
  };

  const handleRejectOrApprove = async ({
    rejectionReason = "",
    externalNotes = "",
    internalNotes = "",
    hasRejectionAttachment = false,
    hasInternalAttachment = false,
    hasExternalAttachment = false,
    rejectionAttachmentName,
    internalAttachmentName,
    externalAttachmentName,
    status,
    reEntryVessel,
    overstayChargesApplicable,
    assignedAnchorageArea,
  }: VesselCallDecisionPayload) => {
    await completeCall({
      service: serviceVesselCall,
      vesselCallId: id || "",
      params: {
        status: status,
        rejectionReason: rejectionReason,
        internalNotes: internalNotes,
        externalNotes: externalNotes,
        rejectionAttachmentsUrl: hasRejectionAttachment ? rejectionAttachmentsUrl : undefined,
        rejectionAttachmentsName: hasRejectionAttachment
          ? (rejectionAttachmentName ?? rejectionAttachmentsName)
          : undefined,
        internalAttachmentsUrl: hasInternalAttachment ? internalAttachmentsUrl : undefined,
        internalAttachmentsName: hasInternalAttachment
          ? (internalAttachmentName ?? internalAttachmentsName)
          : undefined,
        externalAttachmentsUrl: hasExternalAttachment ? externalAttachmentsUrl : undefined,
        externalAttachmentsName: hasExternalAttachment
          ? (externalAttachmentName ?? externalAttachmentsName)
          : undefined,
        reEntryVessel: reEntryVessel,
        overstayChargesApplicable: overstayChargesApplicable,
        submittedBy: undefined,
        submittedByRole: undefined,
        timestamp: new Date().toISOString(),
        assignedAnchorageArea: assignedAnchorageArea ? assignedAnchorageArea : undefined,
      },
    });
    handleCloseAllDrawers();
  };

  const { resumeInterruption } = useResumeVesselCallById({ handleSuccess });

  const handleResumeSubmit = async (payload?: {
    externalNotes: string;
    externalAttachmentsName?: string;
    status?: string;
  }) => {
    await resumeInterruption({
      service: serviceVesselCall,
      vesselCallId: id || "",
      params: {
        status: payload?.status,
        externalNotes: payload?.externalNotes ?? "",
        externalAttachmentsUrl: externalAttachmentsUrl ?? "",
        externalAttachmentsName: payload?.externalAttachmentsName ?? externalAttachmentsName,
      },
    });
    await invalidateInterruption();
    handleCloseAllDrawers();
  };

  const currentStepIndex = (() => {
    const idx = vesselCallStatusSteps.findIndex((s) =>
      window.location.pathname.includes(`/${s.path}`),
    );

    return idx === -1 ? 0 : idx;
  })();

  const { options: companies } = useFormattedReference<Company>(
    `${REFERENCE_DATA_TYPES.COMPANIES}?isShippingAgency=true`,
    "bff-vessel-call",
  );

  const { options: ports } = useFormattedReference<PortCode>(
    REFERENCE_DATA_TYPES.PORT_CODES,
    "bff-vessel-call",
  );

  const agencyName = useMemo(() => {
    return companies.find((c) => c.companyCode === vesselCallDetails?.companyId)
      ?.companyNameEnglish;
  }, [companies, vesselCallDetails]);

  useEffect(() => {
    const active = stepperTabsRef.current?.getActiveStepperTab?.();
    if (active !== currentStepIndex) {
      stepperTabsRef.current?.setStepperTab(currentStepIndex);
    }
  }, [stepperTabsRef, currentStepIndex]);

  // Move hook call above all early returns to avoid conditional hook call
  const { data: vesselRegisterDetailsFixed } = useQuery<VesselRegisterDetailsFixed>({
    queryKey: ["vesselDetails", serviceVesselCall, id],
    queryFn: () => getVesselCallDetails(serviceVesselCall, String(id)),
  });
  const { options: vesselTypes } = useFormattedReference<VesselTypes>(
    REFERENCE_DATA_TYPES.VESSEL_TYPES,
    "bff-vessel",
  );

  const vesselTypeDesc = useMemo(() => {
    const matchingType = vesselTypes?.find(
      ({ vesselTypeCode }) => vesselTypeCode === vesselRegisterDetailsFixed?.vesselTypeCode,
    );
    return matchingType?.vesselTypeDesc;
  }, [vesselTypes, vesselRegisterDetailsFixed?.vesselTypeCode]);

  const dir = i18n.language === "ar" ? "rtl" : "ltr";

  const refetchMovements = async () => {
    queryClient.invalidateQueries({ queryKey: ["getCurrentMovementService"] });
    queryClient.invalidateQueries({ queryKey: ["getPastMovementServices"] });
  };

  const currentMovement = currentMovementsData?.[0];
  const closeVesselCallMovements = [
    ...(currentMovementsData ?? []),
    ...(pastMovementsData?.items ?? []),
  ];
  const canCloseVesselCall =
    !isCurrentMovementsLoading &&
    !isPastMovementsLoading &&
    canShowCloseVesselCallActions(vesselCallDetails?.status, closeVesselCallMovements);
  const isReinstated = vesselCallDetails?.status === VESSEL_CALL_STATUSES.REINSTATED;
  const hasActiveDispute = Boolean(
    activeDisputesData?.items?.some(
      (dispute) => dispute.status === "DISPUTE_RAISED" || dispute.status === "RESOLUTION_PENDING",
    ) || (activeDisputesData?.totalElements ?? 0) > 0,
  );
  const isDisputeStakeholder = Boolean(
    user?.roles?.includes("terminal") || isShippingAgentUser || isMarineDepartmentUser,
  );
  const disputesPath = id
    ? `/${paths.vesselCall.index}/vessel-call-status/${id}/${paths.vesselCall.operations.index}/${paths.vesselCall.operations.disputes}`
    : undefined;

  const vesselHeaderDetails: VesselHeaderDTO = {
    ...buildVesselHeaderDetails(
      id,
      vesselCallDetails,
      t,
      agencyName,
      vesselRegisterDetailsFixed
        ? {
            ...vesselRegisterDetailsFixed,
            vesselTypeDesc: vesselTypeDesc ?? vesselRegisterDetailsFixed.vesselTypeDesc,
          }
        : vesselRegisterDetailsFixed,
    ),
    status: fakeStatus || vesselCallDetails?.status || "",
    originalVesselCallId: vesselCallDetails?.originalVesselCallId,
    isDiplomaticVessel: vesselCallDetails?.diplomaticVessel,
    isBunkerBarge: vesselCallDetails?.canOperateAsBunkerBarge,
    showReinstatedBadge: isReinstated,
    bunkerBargeStartDate: vesselRegisterDetails?.bunkerBargeDetails?.effectiveStartDate,
  };
  const outCtx: VesselCallStatusContextType = {
    vesselRegisterDetails: vesselRegisterDetails!,
    vesselCallDetails: vesselCallDetails!,
    vesselRegisterDetailsFixed: vesselRegisterDetailsFixed ?? null,
    isThirdShippingAgent,
    isOwnersMatter: user?.companyId === vesselCallDetails?.ownersMattersAgent,
    canAccessOperations,
    isOperationsAccessLoading,
    departureAvailability,
    refetchDepartureAvailability,
  };

  const latestClosedMovement = pastMovementsData?.items.find(
    (movement) => movement.status === MOVEMENT_SERVICE_STATUSES.CLOSED,
  );
  const latestClosedMovementTo = latestClosedMovement?.to;
  const isMovementStarted = currentMovement?.status === MOVEMENT_SERVICE_STATUSES.MOVEMENT_STARTED;
  const isMovementClosed =
    latestClosedMovement &&
    (!currentMovement || currentMovement.status !== MOVEMENT_SERVICE_STATUSES.MOVEMENT_STARTED);
  const atSea = "sea".includes(latestClosedMovementTo?.toLowerCase() ?? "");
  const shouldUseVesselLocationForArrival = shouldUseVesselLocationForArrivalStatus(
    latestClosedMovementTo,
    vesselCallDetails?.currentLocation,
  );
  const arrivalStatusLocation = shouldUseVesselLocationForArrival
    ? vesselCallDetails?.currentLocation
    : latestClosedMovementTo;
  const departureApproved = departureDetails?.status === "APPROVED";
  const latestClosedMovementAtc = latestClosedMovement?.atc;
  const isAtcOlderThanSixHours = (() => {
    if (!latestClosedMovementAtc) return false;
    const atcMs =
      latestClosedMovementAtc instanceof Date
        ? latestClosedMovementAtc.getTime()
        : Date.parse(latestClosedMovementAtc);
    if (Number.isNaN(atcMs)) return false;
    const sixHoursMs = 6 * 60 * 60 * 1000;
    return Date.now() - atcMs >= sixHoursMs;
  })();

  const statusOverrideLabel = (() => {
    if (
      vesselCallDetails?.status === "APPROVED" ||
      fakeStatus === "APPROVED" ||
      DEPARTURE_STATUSES.has(vesselCallDetails?.status || "")
    ) {
      if (isMovementStarted) {
        return t("vessel-call.movement-services.status.moving-to", {
          to: currentMovement?.to ?? "-",
        });
      }
      if (isMovementClosed) {
        if (shouldUseVesselLocationForArrival) {
          return t("vessel-call.movement-services.status.arrived-at", {
            to: arrivalStatusLocation ?? "-",
          });
        }
        if (atSea && departureApproved && isAtcOlderThanSixHours) {
          return t("vessel-call.movement-services.status.closed");
        }
        if (atSea && !departureApproved) {
          return t("vessel-call.movement-services.status.arrived-at-sea");
        }
        return t("vessel-call.movement-services.status.arrived-at", {
          to: arrivalStatusLocation ?? "-",
        });
      }
    }
    if (vesselCallDetails?.status === "REINSTATED") {
      if (isMovementStarted) {
        return t("vessel-call.movement-services.status.moving-to", {
          to: currentMovement?.to ?? "-",
        });
      }
    }
    return undefined;
  })();

  const replaceFakeStatus = ((fakeOverrideStatus?: string | null) => {
    if (vesselCallDetails?.status === "APPROVED") {
      if (fakeOverrideStatus === "INTERRUPTED_LEAVE_PORT") {
        return t("vessel-call.status.interrupted-leave-port");
      }
      if (fakeOverrideStatus === "INTERRUPTED_IN_PORT") {
        return t("vessel-call.status.interrupted-in-port");
      }
    }
  })();

  const statusOverrideBadgeType: BadgeType | undefined = (() => {
    if (
      vesselCallDetails?.status === "APPROVED" ||
      fakeStatus === "APPROVED" ||
      DEPARTURE_STATUSES.has(vesselCallDetails?.status || "")
    ) {
      if (isMovementStarted) {
        return "warning";
      }
      if (isMovementClosed) {
        return "success";
      }
    }
    return undefined;
  })();

  const draftExpiresAt = useMemo(() => {
    if (
      (vesselCallDetails?.status === VESSEL_CALL_STATUSES.ARRIVAL_DRAFT ||
        vesselCallDetails?.status === VESSEL_CALL_STATUSES.PRE_ARRIVAL_DRAFT) &&
      vesselCallDetails?.modifiedAt
    ) {
      return addDays(vesselCallDetails.modifiedAt, 3);
    }
    return undefined;
  }, [vesselCallDetails?.status, vesselCallDetails?.modifiedAt]);

  if (isVesselDataLoading || isVesselRegisterDataLoading) {
    return <LoadingOverlay loading={isVesselDataLoading || isVesselRegisterDataLoading} />;
  }

  if (vesselCallError) {
    return <UnauthorizedRedirection />;
  }

  return (
    <PageLayout dir={dir}>
      <VesselCallHeader
        vesselDetails={vesselHeaderDetails}
        openDrawer={handleOpenDrawer}
        refetchVesselCall={refetchVesselCall}
        refetchMovements={refetchMovements}
        showUpdateVesselCall={handleToggleUpdateVesselCall}
        showUpdateButton={showUpdateButton}
        statusOverrideLabel={statusOverrideLabel}
        fakeOverrideStatus={fakeOverrideStatus}
        setFakeOverrideStatus={setFakeOverrideStatus}
        replaceFakeStatus={replaceFakeStatus}
        setFakeStatus={setFakeStatus}
        statusOverrideBadgeType={statusOverrideBadgeType}
        draftExpiresAt={draftExpiresAt}
        canCloseVesselCall={canCloseVesselCall}
        hasActiveDispute={hasActiveDispute && isDisputeStakeholder}
        onViewDisputes={disputesPath ? () => navigate(disputesPath) : undefined}
      />
      <div className="pt-8 pb-8 px-24">
        {canUpdateVesselCall && isUpdateSectionVisible && (
          <UpdateVesselCallSection
            vesselCallDetails={vesselCallDetails}
            ports={ports}
            refetchVesselCall={refetchVesselCall}
            onSubmit={handleUpdateSectionSubmitted}
            onCancel={handleUpdateSectionSubmitted}
          />
        )}

        <StepperTabs
          ref={stepperTabsRef}
          currentStepIndex={currentStepIndex}
          testId={`${TEST_ID_BASE}-vertical-stepper`}
          onStepperTabChange={(index) => {
            if (index === currentStepIndex) return;
            const path = vesselCallStatusSteps[index].path ?? undefined;
            if (!path) return;
            navigate(path);
          }}
          stepperType={STEPPER_TYPE.VERTICAL}
          isTypeChangeable={false}
        >
          {vesselCallStatusSteps.map((step) => (
            <StepperTabPanel key={step.path} label={step.label}>
              <Outlet context={outCtx} />
            </StepperTabPanel>
          ))}
        </StepperTabs>
      </div>
      <PreApproveVesselCallDrawer
        isOpen={openDrawers.preApproveDrawer}
        onClose={handleCloseDrawer}
        onDocumentUpload={(file) => handleDocumentUpload(file, "EXTERNAL_NOTES")}
        onAdditionalDocumentUpload={(file) => handleDocumentUpload(file, "INTERNAL_NOTES")}
        onSubmit={handleRejectOrApprove}
      />
      <PreRejectVesselCallDrawer
        isOpen={openDrawers.preRejectDrawer}
        onClose={handleCloseDrawer}
        onDocumentUpload={(file) => handleDocumentUpload(file, "REJECTION_NOTES")}
        onAdditionalDocumentUpload={(file) => handleDocumentUpload(file, "INTERNAL_NOTES")}
        onSubmit={handleRejectOrApprove}
      />
      <ApproveVesselCallDrawer
        isOpen={openDrawers.approveDrawer}
        onClose={handleCloseDrawer}
        onDocumentUpload={(file) => handleDocumentUpload(file, "EXTERNAL_NOTES")}
        onAdditionalDocumentUpload={(file) => handleDocumentUpload(file, "INTERNAL_NOTES")}
        onSubmit={handleRejectOrApprove}
        reEntryVessel={vesselCallDetails?.reEntryVessel}
        overstayChargesApplicable={vesselCallDetails?.overstayChargesApplicable}
        expectedArrivalLocation={vesselCallDetails?.expectedArrivalLocation}
        preferredAnchorageArea={
          vesselCallDetails?.status === "PENDING_APPROVAL"
            ? vesselCallDetails?.preferredAnchorageArea?.anchorageAreaCode
            : undefined
        }
      />
      <RejectVesselCallDrawer
        isOpen={openDrawers.rejectDrawer}
        onClose={handleCloseDrawer}
        onDocumentUpload={(file) => handleDocumentUpload(file, "REJECTION_NOTES")}
        onAdditionalDocumentUpload={(file) => handleDocumentUpload(file, "INTERNAL_NOTES")}
        onSubmit={handleRejectOrApprove}
      />
      <StatusSideDrawer
        isOpen={openDrawers.statusDrawer}
        onClose={handleCloseDrawer}
        submittedBy={vesselCallDetails?.lastTaskDecision?.submittedBy}
        submittedByRole={vesselCallDetails?.lastTaskDecision?.submittedByRole}
        createdAt={vesselCallDetails?.lastTaskDecision?.timestamp}
        externalNotes={vesselCallDetails?.lastTaskDecision?.externalNotes}
        externalNotesDocument={vesselCallDetails?.lastTaskDecision?.externalAttachmentsUrl}
        externalNotesDocumentName={vesselCallDetails?.lastTaskDecision?.externalAttachmentsName}
        rejectionReason={vesselCallDetails?.lastTaskDecision?.rejectionReason}
        rejectionAttachmentsUrl={vesselCallDetails?.lastTaskDecision?.rejectionAttachmentsUrl}
        rejectionAttachmentsName={vesselCallDetails?.lastTaskDecision?.rejectionAttachmentsName}
        internalNotes={vesselCallDetails?.lastTaskDecision?.internalNotes}
        internalNotesDocument={vesselCallDetails?.lastTaskDecision?.internalAttachmentsUrl}
        internalNotesDocumentName={vesselCallDetails?.lastTaskDecision?.internalAttachmentsName}
      />
      <CloseVesselCallDrawer
        isOpen={openDrawers.closeVesselCallDrawer}
        onClose={handleCloseDrawer}
        onDocumentUpload={(file) => handleDocumentUpload(file, "EXTERNAL_NOTES")}
        onAdditionalDocumentUpload={(file) => handleDocumentUpload(file, "INTERNAL_NOTES")}
        onSubmit={handleCloseVesselCall}
      />
      <InterruptVesselCallDrawer
        isOpen={openDrawers.interruptDrawer}
        onClose={handleCloseDrawer}
        internalAttachmentUrl={internalAttachmentsUrl}
        internalAttachmentName={internalAttachmentsName}
        externalAttachmentUrl={externalAttachmentsUrl}
        externalAttachmentName={externalAttachmentsName}
        onDocumentUpload={(file) => handleDocumentUpload(file, "EXTERNAL_NOTES")}
        onAdditionalDocumentUpload={(file) => handleDocumentUpload(file, "INTERNAL_NOTES")}
        onSubmit={handleInterruptClick}
      />
      <ResumeVesselCallDrawer
        isOpen={openDrawers.resumeVesselCallDrawer}
        onClose={handleCloseDrawer}
        onDocumentUpload={(file) => handleDocumentUpload(file, "EXTERNAL_NOTES")}
        onSubmit={handleResumeSubmit}
      />
      <GenericDialog
        open={isInterruptConfirmOpen}
        title={t("vessel-call.interrupt.confirm-title")}
        content=""
        cancelButtonText={t("common.cancel")}
        confirmButtonText={t("vessel-call.interrupt.interrupt-button")}
        handleCloseDialog={() => setIsInterruptConfirmOpen(false)}
        confirmAction={handleConfirmInterrupt}
        testId="interrupt-vessel-call-confirm-dialog"
        maxWidth="sm"
      />
    </PageLayout>
  );
};

export default VesselCallStatus;
